// Live Palworld TCG auction board for a single card.
//
// Before this existed, the only way to learn what a card was actually bid to was
// to click an eBay link and leave Pal Stars. This panel puts the auction market
// on the page: raw singles and certified slabs in separate tabs, each row
// showing current bid, bid count, time remaining, shipping, grade and seller
// standing, with the click-through as the last step rather than the first.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Gavel, ExternalLink, Tag } from "lucide-react";
import { getCardAuctions } from "@/lib/auctions.functions";
import { getCardTcgplayer } from "@/lib/tcgplayer.functions";
import type { AuctionBoard, AuctionListing } from "@/lib/auctions-shared";
import type { TcgplayerBoard } from "@/lib/tcgplayer-shared";

import { formatMoney, type CurrencyCode } from "@/lib/prices-shared";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { InfoHint } from "@/components/InfoHint";

/** "2h 14m" style countdown to an auction close. */
function timeLeft(iso: string | null): string {
  if (!iso) return "no end date";
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms)) return "—";
  if (ms <= 0) return "ended";
  const m = Math.floor(ms / 60000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function Row({ l, currency, code }: { l: AuctionListing; currency: CurrencyCode; code: string }) {
  const ending = l.kind === "auction" && Date.parse(l.endsAt ?? "") - Date.now() < 6 * 3600 * 1000;
  return (
    <li className="rounded-xl border border-border/60 bg-card/40 p-2.5">
      <div className="flex items-start gap-2.5">
        {l.image ? (
          <img
            src={l.image}
            alt=""
            loading="lazy"
            width={44}
            height={60}
            className="h-[60px] w-11 shrink-0 rounded-md object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-xs font-semibold leading-snug">{l.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-semibold text-foreground">
              {l.kind === "auction" ? <Gavel className="h-3 w-3" /> : <Tag className="h-3 w-3" />}
              {formatMoney(l.usd, currency)}
            </span>
            {l.kind === "auction" ? (
              <>
                <span className="tabular-nums">{l.bids ?? 0} bid{l.bids === 1 ? "" : "s"}</span>
                <span className={ending ? "font-semibold text-amber-300" : ""}>{timeLeft(l.endsAt)} left</span>
              </>
            ) : (
              <span>Buy It Now{l.bestOffer ? " · offers" : ""}</span>
            )}
            {l.grade ? <span className="font-semibold text-amber-300">{l.grade}</span> : null}
            <span>
              {l.shippingUsd === null
                ? "shipping at checkout"
                : l.shippingUsd === 0
                  ? "free shipping"
                  : `+${formatMoney(l.shippingUsd, currency)} ship`}
            </span>
            {l.raw.currency !== "USD" ? (
              <span>
                listed {l.raw.currency} {l.raw.value.toFixed(2)}
              </span>
            ) : null}
            {l.sellerFeedbackPct !== null ? <span>{l.sellerFeedbackPct}% seller</span> : null}
            {l.location && l.location !== "US" ? <span>{l.location}</span> : null}
          </div>
        </div>
        <a
          href={l.url}
          target="_blank"
          rel="nofollow ugc noopener noreferrer"
          onClick={() => trackAffiliateClick({ source: "ebay", kind: "card", targetKey: `${code}-auction` })}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:brightness-110"
        >
          {l.kind === "auction" ? "Bid" : "View"}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </li>
  );
}

function Stats({ list, currency }: { list: AuctionListing[]; currency: CurrencyCode }) {
  const auctions = list.filter((l) => l.kind === "auction");
  const bins = list.filter((l) => l.kind === "bin");
  const bids = auctions.reduce((n, a) => n + (a.bids ?? 0), 0);
  const cheapest = bins.length ? Math.min(...bins.map((b) => b.usd)) : null;
  const topBid = auctions.length ? Math.max(...auctions.map((a) => a.usd)) : null;
  return (
    <dl className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
      {[
        { label: "Live auctions", value: String(auctions.length), hint: "Timed listings open for bidding right now." },
        { label: "Total bids", value: String(bids), hint: "Bids placed across those auctions — real demand, not asking prices." },
        { label: "Top live bid", value: topBid !== null ? formatMoney(topBid, currency) : "—", hint: "Highest current bid across the open auctions." },
        { label: "Cheapest BIN", value: cheapest !== null ? formatMoney(cheapest, currency) : "—", hint: "Lowest Buy It Now ask — the price you can pay instantly." },
      ].map((s) => (
        <div key={s.label} className="rounded-lg border border-border/60 bg-card/40 px-2.5 py-2">
          <p className="uppercase tracking-wide text-muted-foreground">
            {s.label}
            <InfoHint label={s.label}>{s.hint}</InfoHint>
          </p>
          <p className="text-sm font-bold tabular-nums">{s.value}</p>
        </div>
      ))}
    </dl>
  );
}

/** Live TCGplayer seller offers + completed sales for this printing. */
function TcgplayerPanel({ code, currency }: { code: string; currency: CurrencyCode }) {
  const { data, isLoading } = useQuery<TcgplayerBoard>({
    queryKey: ["card-tcgplayer", code.toUpperCase()],
    queryFn: () => getCardTcgplayer({ data: { code } }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading) return <p className="mt-2 text-xs text-muted-foreground">Reading TCGplayer listings for {code}…</p>;
  if (!data) return <p className="mt-2 text-xs text-muted-foreground">TCGplayer listings unavailable right now.</p>;

  return (
    <>
      <dl className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
        {[
          { label: "TCGplayer market", value: data.market !== null ? formatMoney(data.market, currency) : "—", hint: "TCGplayer's own market price for this printing — their rolling average of completed sales." },
          { label: "Lowest ask", value: data.lowest !== null ? formatMoney(data.lowest, currency) : "—", hint: "Cheapest seller price listed on TCGplayer right now." },
          { label: "Live listings", value: String(data.totalListings), hint: "How many TCGplayer sellers have this printing in stock — the depth behind the price." },
          { label: "Recent sold avg", value: data.soldAvg !== null ? formatMoney(data.soldAvg, currency) : "—", hint: "Average of TCGplayer's most recent completed sales for this printing — money that actually changed hands." },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border/60 bg-card/40 px-2.5 py-2">
            <p className="uppercase tracking-wide text-muted-foreground">
              {s.label}
              <InfoHint label={s.label}>{s.hint}</InfoHint>
            </p>
            <p className="text-sm font-bold tabular-nums">{s.value}</p>
          </div>
        ))}
      </dl>

      {data.listings.length ? (
        <ul className="mt-2 grid gap-1.5">
          {data.listings.map((l) => (
            <li key={l.id} className="rounded-xl border border-border/60 bg-card/40 p-2.5">
              <div className="flex items-start justify-between gap-2.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-snug">
                    {l.condition}
                    {l.printing !== "Normal" ? ` · ${l.printing}` : ""}
                    {l.language !== "English" ? ` · ${l.language}` : ""}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                      <Tag className="h-3 w-3" />
                      {formatMoney(l.price, currency)}
                    </span>
                    <span>{l.shipping === 0 ? "free shipping" : `+${formatMoney(l.shipping, currency)} ship`}</span>
                    <span className="font-semibold text-foreground">{formatMoney(l.total, currency)} delivered</span>
                    <span>{l.quantity} available</span>
                    <span>{l.sellerName}</span>
                    {l.sellerRating !== null ? <span>{l.sellerRating}% seller</span> : null}
                    {l.directSeller ? <span className="font-semibold text-emerald-300">Direct</span> : null}
                  </div>
                </div>
                <a
                  href={l.url}
                  target="_blank"
                  rel="nofollow ugc noopener noreferrer"
                  onClick={() => trackAffiliateClick({ source: "tcgplayer", kind: "card", targetKey: `${code}-listing` })}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:brightness-110"
                >
                  Buy
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{data.note}</p>
      )}

      {data.sales.length ? (
        <div className="mt-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            Latest TCGplayer sales
            <InfoHint label="Latest TCGplayer sales">
              Completed TCGplayer orders for this printing, newest first — the sold prices, not asking prices.
            </InfoHint>
          </p>
          <ul className="mt-1.5 grid gap-1">
            {data.sales.slice(0, 8).map((s, i) => (
              <li
                key={`${s.at}-${i}`}
                className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 rounded-lg border border-border/60 bg-card/30 px-2.5 py-1.5 text-[11px] text-muted-foreground"
              >
                <span className="font-semibold tabular-nums text-foreground">{formatMoney(s.price, currency)}</span>
                <span>{s.condition}</span>
                {s.printing !== "Normal" ? <span>{s.printing}</span> : null}
                {s.quantity > 1 ? <span>x{s.quantity}</span> : null}
                {s.at ? <span className="ml-auto">{new Date(s.at).toLocaleDateString()}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="mt-2 text-[10px] text-muted-foreground">
        TCGplayer data for {data.productName ?? code}
        {data.setName ? ` · ${data.setName}` : ""}, cached for 10 minutes per card. Links are affiliate links; Pal Stars
        may earn a commission. TCGplayer market and sold prices feed the Pal Stars price chart and index alongside eBay.
      </p>
    </>
  );
}

export function CardAuctionBoard({ code, currency }: { code: string; currency: CurrencyCode }) {
  const [tab, setTab] = useState<"raw" | "graded" | "asian" | "tcgplayer">("tcgplayer");
  const { data, isLoading } = useQuery<AuctionBoard>({
    queryKey: ["card-auctions", code.toUpperCase()],
    queryFn: () => getCardAuctions({ data: { code } }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const list = data && tab !== "tcgplayer" ? (tab === "raw" ? data.raw : tab === "graded" ? data.graded : data.asian) : [];

  return (
    <section className="mt-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Live auctions &amp; Buy It Now
          <InfoHint label="Live auctions">
            Read straight from eBay for this exact printing when you open the card, cached for 10 minutes per card.
            Auctions show the current bid, not the asking price, so you can see what buyers are really paying before
            clicking through. The TCGplayer tab does the same for TCGplayer's marketplace.
          </InfoHint>
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {(["tcgplayer", "raw", "graded", "asian"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              aria-pressed={tab === t}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                tab === t ? "bg-primary/20 text-primary" : "border border-border/60 text-muted-foreground hover:bg-card"
              }`}
            >
              {t === "tcgplayer"
                ? "TCGplayer"
                : t === "raw"
                  ? `eBay (${data ? data.raw.length : "…"})`
                  : t === "graded"
                    ? `Graded (${data ? data.graded.length : "…"})`
                    : `JP/CN/KR (${data ? data.asian.length : "…"})`}
            </button>
          ))}
        </div>
      </div>

      {tab === "tcgplayer" ? (
        <TcgplayerPanel code={code} currency={currency} />
      ) : isLoading ? (

        <p className="mt-2 text-xs text-muted-foreground">Reading live eBay auctions for {code}…</p>
      ) : !data || data.degraded ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {data?.note ?? "Live auction feed unavailable right now."} The marketplace links below always work.
        </p>
      ) : (
        <>
          <Stats list={list} currency={currency} />
          {list.length ? (
            <ul className="mt-2 grid gap-1.5">
              {list.map((l) => (
                <Row key={l.id} l={l} currency={currency} code={code} />
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              {tab === "graded"
                ? "No certified slab of this printing is listed right now. Graded supply is thin, so slabs appear and sell fast."
                : tab === "asian"
                  ? "No Japanese, Chinese or Korean copy of this printing is listed right now. Use the JP/CN/KR eBay link below for the broadest live search."
                : "No raw copy of this printing is listed right now — thinly traded cards go days without a listing."}
            </p>
          )}
          <p className="mt-2 text-[10px] text-muted-foreground">
            eBay listings for {code}, validated against this printing. Auction rows show the current bid plus bid count
            and time remaining; Buy It Now rows show the ask. Links are affiliate links; Pal Stars may earn a commission.
            Japanese, Chinese and Korean listings are purchase options only and never affect English price data, charts or the index.
          </p>
        </>
      )}
    </section>
  );
}
